import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  Input,
  Select,
  Tag,
  Button,
  Dropdown,
  Modal,
  Descriptions,
  message,
  Switch,
  Tooltip,
  Drawer,
  Form,
  InputNumber,
  Empty,
  DatePicker
} from 'antd';
import { PlusOutlined, DownloadOutlined, FileExcelOutlined, SearchOutlined, MobileOutlined, GlobalOutlined, UserOutlined, ApiOutlined, CalendarOutlined, ClockCircleOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faEllipsisV,
  faEye,
  faEdit,
  faArchive,
  faFilter,
  faUserEdit,
  faSliders
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { 
  BookingFilters, 
  BookingTable,
  TabContentWrapper,
  AnimatedFiltersRow,
  AnimatedTableSection,
  DesktopTableWrapper,
  MobileBookingCards,
  BookingCard,
  BookingCardStatus,
  MobileCardsPagination,
  MobileFilterBar,
  DesktopFilters,
  StatusChipsRow,
  FilterChip
} from './NewStyled';
import BookingService from '../../services/BookingService';
import EventTypeService from '../../services/EventTypeService';
import PatientService from '../../services/PatientService';
import EditBookingModal from '../../components/EditBookingModal';
import BookingFilterDrawer from './components/BookingFilterDrawer';
import CreateBookingModal from '../../components/CreateBookingModal';
import { COLORS } from '../../constants/colors';
import { ROUTES } from '../../constants/routes';
import HeartLoader from '../../components/shared/HeartLoader';

const { RangePicker } = DatePicker;

// Status pill colors for the mobile booking cards.
const STATUS_PILL = {
  pending:   { label: 'Pending',   bg: '#fff7e6', fg: '#d48806' },
  confirmed: { label: 'Confirmed', bg: '#e6f4ff', fg: '#0958d9' },
  completed: { label: 'Completed', bg: '#f6ffed', fg: '#389e0d' },
  cancelled: { label: 'Cancelled', bg: '#fff1f0', fg: '#cf1322' },
  'no-show': { label: 'No Show',   bg: '#fff2e8', fg: '#d4380d' },
  archived:  { label: 'Archived',  bg: '#f5f5f5', fg: '#595959' }
};

// Quick status filter chips shown on mobile above the booking cards.
const STATUS_CHIPS = [
  { value: undefined, label: 'All',       countKey: 'totalBookings',     color: null },
  { value: 'confirmed', label: 'Confirmed', countKey: 'confirmedBookings', color: '#0958d9' },
  { value: 'pending',   label: 'Pending',   countKey: 'pendingBookings',   color: '#d48806' },
  { value: 'completed', label: 'Completed', countKey: 'completedBookings', color: '#389e0d' },
  { value: 'cancelled', label: 'Cancelled', countKey: 'cancelledBookings', color: '#cf1322' }
];

// Human label for a booking's origin, used in table/exports.
const sourceLabelOf = (source) => {
  switch (source) {
    case 'mobile': return 'App';
    case 'admin': return 'Admin';
    case 'api': return 'API';
    default: return 'Website';
  }
};

const BookingManager = ({ initialEventTypeFilter, onStatsChange, stats }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);

  // Members drawer state — opened by clicking an event row
  const [membersDrawerVisible, setMembersDrawerVisible] = useState(false);
  const [membersDrawerBooking, setMembersDrawerBooking] = useState(null);
  const [membersDrawerVisits, setMembersDrawerVisits] = useState([]);
  const [membersDrawerLoading, setMembersDrawerLoading] = useState(false);
  const [membersDrawerSearch, setMembersDrawerSearch] = useState('');
  const [eventTypes, setEventTypes] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Publish-to-app popup state. Bookings from the public page lack mobile-event
  // fields (pay, staffing), so we collect them before publishing.
  const [appPublishForm] = Form.useForm();
  const [appModalVisible, setAppModalVisible] = useState(false);
  const [appModalBooking, setAppModalBooking] = useState(null);
  const [appPublishing, setAppPublishing] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });
  const [sortField, setSortField] = useState('bookingDate');
  const [sortOrder, setSortOrder] = useState('ascend');
  const [filters, setFilters] = useState({
    search: '',
    status: undefined, // Start with all statuses showing
    eventType: undefined,
    dateRange: null // [startDayjs, endDayjs] for filtering by event date
  });
  const [exporting, setExporting] = useState(false);

  // Apply initial event type filter when provided
  useEffect(() => {
    if (initialEventTypeFilter) {
      setFilters(prev => ({ ...prev, eventType: initialEventTypeFilter }));
      setFilterDrawerVisible(false);
    } else if (initialEventTypeFilter === null) {
      // Clear the filter when explicitly set to null
      setFilters(prev => ({ ...prev, eventType: undefined }));
    }
  }, [initialEventTypeFilter]);

  useEffect(() => {
    loadBookings();
    loadEventTypes();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadBookings();
    }, 300); // Debounce filter changes
    
    return () => clearTimeout(timeoutId);
  }, [filters.search, filters.status, filters.eventType, filters.dateRange]);

  // Reload when pagination or sorting changes
  useEffect(() => {
    loadBookings();
  }, [pagination.page, pagination.limit, sortField, sortOrder]);

  // Convert the selected date range into inclusive start/end-of-day ISO strings.
  // The picker hands back dayjs objects (antd v5), so startOf/endOf/toISOString apply.
  const getDateRangeParams = () => {
    const range = filters.dateRange;
    return {
      startDate: range && range[0] ? range[0].startOf('day').toISOString() : undefined,
      endDate: range && range[1] ? range[1].endOf('day').toISOString() : undefined
    };
  };

  const loadBookings = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search,
        status: filters.status,
        eventTypeId: filters.eventType, // Backend expects eventTypeId not eventType
        ...getDateRangeParams(),
        sortField: sortField,
        sortOrder: sortOrder === 'ascend' ? 'asc' : 'desc'
      };
      
      // Only include archived bookings when explicitly filtering for 'archived' status
      // Otherwise, exclude archived by default for a cleaner view
      if (filters.status === 'archived') {
        params.includeArchived = 'true';
      } else {
        params.includeArchived = 'false';
      }
      
      const response = await BookingService.getAllBookings(params);
      
      if (response && response.success && response.data && Array.isArray(response.data.bookings)) {
        // Past-cancelled bookings are now excluded server-side so the
        // pagination total matches what we actually render.
        setBookings(response.data.bookings);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0
        }));
      } else {
        console.error('Invalid response structure:', response);
        // If no bookings or error, show empty state instead of error message
        setBookings([]);
        setPagination(prev => ({
          ...prev,
          total: 0
        }));
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
      // Show empty state instead of error for now
      setBookings([]);
      setPagination(prev => ({
        ...prev,
        total: 0
      }));
    } finally {
      setLoading(false);
    }
  };

  const loadEventTypes = async () => {
    try {
      const response = await EventTypeService.getEventTypes();
      
      if (response.success && response.data) {
        // Handle both array and object with eventTypes property
        const eventTypesArray = Array.isArray(response.data) 
          ? response.data 
          : response.data.eventTypes || [];
          
        if (Array.isArray(eventTypesArray)) {
          setEventTypes(eventTypesArray);
        } else {
          console.error('Event types is not an array:', eventTypesArray);
          setEventTypes([]);
        }
      } else {
        console.error('Event types response error:', response);
        setEventTypes([]);
      }
    } catch (error) {
      console.error('Error loading event types:', error);
      setEventTypes([]);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    // Reset to first page when filter changes
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
  };

  const hasActiveFilters = () => {
    return filters.status || filters.eventType || filters.search || filters.dateRange;
  };

  const exportToCSV = (data, filename = 'bookings_export.csv') => {
    if (!data || data.length === 0) {
      message.warning('No data to export');
      return;
    }

    // Define CSV headers
    const headers = [
      'Client Name',
      'Email',
      'Phone',
      'Service',
      'Date',
      'Time',
      'Status',
      'Source',
      'Booking Code',
      'People Screened',
      'Notes'
    ];

    // Convert data to CSV format
    const csvData = data.map(booking => [
      `${booking.userInfo.firstName} ${booking.userInfo.lastName}`,
      booking.userInfo.email || '',
      booking.userInfo.phone || '',
      booking.eventType.name || '',
      booking.timeSlot ? moment(booking.timeSlot.date).format('YYYY-MM-DD') : '',
      booking.timeSlot ? moment(booking.timeSlot.startTime).format('HH:mm') : '',
      booking.status || '',
      sourceLabelOf(booking.source),
      booking.bookingCode || '',
      booking.membersScreened ?? 0,
      booking.notes || ''
    ]);

    // Combine headers and data
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success(`Exported ${data.length} booking(s) to CSV`);
    }
  };

  const exportToExcel = (data, filename = 'bookings_export.xlsx') => {
    if (!data || data.length === 0) {
      message.warning('No data to export');
      return;
    }

    const worksheetData = data.map(booking => ({
      'Client Name': `${booking.userInfo?.firstName || ''} ${booking.userInfo?.lastName || ''}`.trim(),
      'Email': booking.userInfo?.email || '',
      'Phone': booking.userInfo?.phone || '',
      'Service': booking.eventType?.name || '',
      'Event Name': booking.eventLocation?.eventName || booking.userInfo?.customData?.businessEventName || '',
      'Date': booking.bookingDate ? moment(booking.bookingDate).format('YYYY-MM-DD') : '',
      'End Date': booking.endDate && booking.isMultiDay ? moment(booking.endDate).format('YYYY-MM-DD') : '',
      'Time': booking.timeSlot ? `${moment(booking.timeSlot.startTime).format('h:mm A')} - ${moment(booking.timeSlot.endTime).format('h:mm A')}` : '',
      'Status': booking.status || '',
      'Source': sourceLabelOf(booking.source),
      'Booking Code': booking.bookingCode || '',
      'People Screened': booking.membersScreened ?? 0,
      'Booked On': booking.createdAt ? moment(booking.createdAt).format('YYYY-MM-DD h:mm A') : '',
      'Notes': booking.notes || ''
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Bookings');

    // Auto-size columns
    const colWidths = Object.keys(worksheetData[0]).map(key => ({
      wch: Math.min(40, Math.max(key.length, ...worksheetData.map(row => String(row[key] || '').length)) + 2)
    }));
    worksheet['!cols'] = colWidths;

    XLSX.writeFile(workbook, filename);
    message.success(`Exported ${data.length} booking(s) to Excel`);
  };

  const getExportData = () => {
    if (selectedRowKeys.length > 0) {
      return bookings.filter(booking => selectedRowKeys.includes(booking._id));
    }
    return bookings;
  };

  // Fetch every booking that matches the current filters (search, status, event
  // type and date range) across all pages so exports include the full set, not
  // just the page currently visible in the table.
  const fetchAllMatchingBookings = async () => {
    const pageLimit = 100;
    let page = 1;
    let total = Infinity;
    const all = [];

    while (all.length < total) {
      const params = {
        page,
        limit: pageLimit,
        search: filters.search,
        status: filters.status,
        eventTypeId: filters.eventType,
        ...getDateRangeParams(),
        sortField,
        sortOrder: sortOrder === 'ascend' ? 'asc' : 'desc',
        includeArchived: filters.status === 'archived' ? 'true' : 'false'
      };

      const response = await BookingService.getAllBookings(params);

      if (!(response && response.success && response.data && Array.isArray(response.data.bookings))) {
        break;
      }

      all.push(...response.data.bookings);
      total = response.data.pagination?.total ?? all.length;

      if (response.data.bookings.length === 0) break;
      page += 1;
      if (page > 1000) break; // hard safety stop
    }

    return all;
  };

  const handleExport = async (format = 'csv') => {
    const dateSuffix = moment().format('YYYY-MM-DD');

    // When specific rows are selected, export only those (current page selection).
    if (selectedRowKeys.length > 0) {
      const data = getExportData();
      if (!data || data.length === 0) {
        message.warning('No bookings to export');
        return;
      }
      if (format === 'excel') {
        exportToExcel(data, `selected_bookings_${dateSuffix}.xlsx`);
      } else {
        exportToCSV(data, `selected_bookings_${dateSuffix}.csv`);
      }
      return;
    }

    // Otherwise export every booking matching the current filters / date range.
    setExporting(true);
    const hideLoading = message.loading('Preparing export…', 0);
    try {
      const data = await fetchAllMatchingBookings();
      if (!data || data.length === 0) {
        message.warning('No bookings to export');
        return;
      }
      if (format === 'excel') {
        exportToExcel(data, `all_bookings_${dateSuffix}.xlsx`);
      } else {
        exportToCSV(data, `all_bookings_${dateSuffix}.csv`);
      }
    } catch (error) {
      console.error('Error exporting bookings:', error);
      message.error('Failed to export bookings');
    } finally {
      hideLoading();
      setExporting(false);
    }
  };

  const handleExportCSV = () => handleExport('csv');
  const handleExportExcel = () => handleExport('excel');

  const handleLegacyExport = () => handleExport('csv');

  // Legacy export kept for filter drawer backward compatibility

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
    },
    getCheckboxProps: (record) => ({
      name: record._id,
    }),
  };

  const handleTableChange = (paginationInfo, filters, sorter) => {
    setPagination(prev => ({
      ...prev,
      page: paginationInfo.current,
      limit: paginationInfo.pageSize
    }));
    
    // Handle sorting - use columnKey as fallback since columns without dataIndex won't have sorter.field
    const sortKey = sorter?.field || sorter?.columnKey;
    if (sorter && sortKey) {
      setSortField(sortKey);
      setSortOrder(sorter.order || 'ascend');
    }
  };

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
    setDetailsModalVisible(true);
  };

  const handleCloseDetailsModal = () => {
    setDetailsModalVisible(false);
    setSelectedBooking(null);
  };

  // Open right-side drawer showing all members tied to this booking
  const handleRowClick = (booking) => {
    setMembersDrawerBooking(booking);
    setMembersDrawerSearch('');
    setMembersDrawerVisits([]);
    setMembersDrawerVisible(true);
    loadDrawerMembers(booking._id);
  };

  const loadDrawerMembers = async (bookingId) => {
    if (!bookingId) return;
    setMembersDrawerLoading(true);
    try {
      const response = await PatientService.getPatientQueue(bookingId, { status: 'all' });
      if (response && response.success && Array.isArray(response.data)) {
        setMembersDrawerVisits(response.data);
      } else {
        setMembersDrawerVisits([]);
      }
    } catch (err) {
      console.error('Error loading members for booking:', err);
      message.error('Failed to load members for this event');
      setMembersDrawerVisits([]);
    } finally {
      setMembersDrawerLoading(false);
    }
  };

  const handleMemberClick = (visit) => {
    if (!visit?.patientId) {
      message.warning('No patient record linked to this entry');
      return;
    }
    navigate(ROUTES.ADMIN_MEMBER_RECORD_EDIT.replace(':patientId', visit.patientId));
  };

  const handleEditBooking = (booking) => {
    setSelectedBooking(booking);
    setEditModalVisible(true);
  };

  const handleCloseEditModal = () => {
    setEditModalVisible(false);
    setSelectedBooking(null);
  };

  const handleEditSuccess = (updatedBooking) => {
    // Reload bookings to reflect changes
    loadBookings();
    // Also update the selected booking if details modal is open
    if (selectedBooking && selectedBooking._id === updatedBooking._id) {
      setSelectedBooking(updatedBooking);
    }
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      const response = await BookingService.updateBookingStatus(bookingId, newStatus);
      
      if (response.success) {
        message.success('Status updated successfully');
        
        // Update the selected booking
        if (selectedBooking && selectedBooking._id === bookingId) {
          setSelectedBooking(prev => ({
            ...prev,
            status: newStatus
          }));
        }
        
        // Reload the bookings list to reflect the change
        loadBookings();
        
        // Refresh stats in parent component
        if (onStatsChange) {
          onStatsChange();
        }
      } else {
        message.error(response.error || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      message.error('Failed to update status');
    }
  };

  const handleArchiveBooking = async (booking) => {
    try {
      const response = await BookingService.archiveBooking(booking._id);
      
      if (response.success) {
        message.success('Booking archived successfully');
        
        // Close modal if the archived booking is currently selected
        if (selectedBooking && selectedBooking._id === booking._id) {
          handleCloseDetailsModal();
        }
        
        // Reload the bookings list to reflect the change
        loadBookings();
      } else {
        message.error(response.error || 'Failed to archive booking');
      }
    } catch (error) {
      console.error('Error archiving booking:', error);
      message.error('Failed to archive booking');
    }
  };

  // Handle pre-registration toggle for an event/booking
  const handlePreRegistrationToggle = async (booking, enabled) => {
    try {
      const response = await BookingService.togglePreRegistration(booking._id, enabled);
      
      if (response.success) {
        message.success(`Pre-registration ${enabled ? 'enabled' : 'disabled'} successfully`);
        // Update local state
        setBookings(prev => prev.map(b => 
          b._id === booking._id 
            ? { ...b, allowPreRegistration: enabled } 
            : b
        ));
      } else {
        message.error(response.error || 'Failed to toggle pre-registration');
      }
    } catch (error) {
      console.error('Error toggling pre-registration:', error);
      message.error('Failed to toggle pre-registration');
    }
  };

  const applyAppVisibilityToState = (bookingId, enabled, appEventId) => {
    setBookings(prev => prev.map(b => (
      b._id === bookingId
        ? {
            ...b,
            appEnabled: enabled,
            appEventId: appEventId || b.appEventId || null
          }
        : b
    )));
  };

  const handleAppVisibilityToggle = async (booking, enabled) => {
    // Enabling: open the publish popup to collect mobile-event fields.
    if (enabled) {
      setAppModalBooking(booking);
      setAppModalVisible(true);
      appPublishForm.resetFields();
      appPublishForm.setFieldsValue({ payType: 'hourly', requiredStaff: 1 });
      return;
    }

    // Disabling: no popup, just unpublish.
    try {
      const response = await BookingService.toggleAppVisibility(booking._id, false);
      if (response.success) {
        message.success('App visibility disabled');
        applyAppVisibilityToState(booking._id, false, response.data?.appEventId);
      } else {
        message.error(response.error || 'Failed to update app visibility');
      }
    } catch (error) {
      console.error('Error toggling app visibility:', error);
      message.error('Failed to update app visibility');
    }
  };

  const handleAppPublishSubmit = async () => {
    if (!appModalBooking) return;
    let values;
    try {
      values = await appPublishForm.validateFields();
    } catch {
      return;
    }

    setAppPublishing(true);
    try {
      const response = await BookingService.toggleAppVisibility(
        appModalBooking._id,
        true,
        {
          payRate: values.payRate,
          payType: values.payType,
          estimatedHours: values.estimatedHours,
          requiredStaff: values.requiredStaff,
          estimatedAttendees: values.estimatedAttendees
        }
      );
      if (response.success) {
        message.success('Event published to the app');
        applyAppVisibilityToState(appModalBooking._id, true, response.data?.appEventId);
        setAppModalVisible(false);
        setAppModalBooking(null);
        appPublishForm.resetFields();
      } else {
        message.error(response.error || 'Failed to publish event to the app');
      }
    } catch (error) {
      console.error('Error publishing event to app:', error);
      message.error(error?.response?.data?.message || 'Failed to publish event to the app');
    } finally {
      setAppPublishing(false);
    }
  };

  const handleAppPublishCancel = () => {
    setAppModalVisible(false);
    setAppModalBooking(null);
    appPublishForm.resetFields();
  };


  const handleMenuClick = (key, record) => {
    switch (key) {
      case 'view':
        handleViewDetails(record);
        break;
      case 'edit':
        handleEditBooking(record);
        break;
      case 'archive':
        handleArchiveBooking(record);
        break;
      default:
        break;
    }
  };

  const getMenuItems = (record) => [
    {
      key: 'view',
      label: 'View Details',
      icon: <FontAwesomeIcon icon={faEye} />,
      onClick: () => handleMenuClick('view', record)
    },
    {
      key: 'edit',
      label: 'Edit',
      icon: <FontAwesomeIcon icon={faEdit} />,
      onClick: () => handleMenuClick('edit', record)
    },
    {
      key: 'archive',
      label: 'Archive',
      icon: <FontAwesomeIcon icon={faArchive} />,
      onClick: () => handleMenuClick('archive', record)
    }
  ];

  // Renders where a booking originated as an icon; hover shows the full label.
  const renderSource = (source) => {
    const map = {
      mobile: { color: '#2f54eb', icon: <MobileOutlined />, label: 'Booked via App' },
      web: { color: '#8c8c8c', icon: <GlobalOutlined />, label: 'Booked via Website' },
      admin: { color: '#722ed1', icon: <UserOutlined />, label: 'Created by Admin' },
      api: { color: '#8c8c8c', icon: <ApiOutlined />, label: 'Created via API' }
    };
    const m = map[source] || map.web;
    return (
      <Tooltip title={m.label}>
        <span style={{ color: m.color, fontSize: 18, display: 'inline-flex' }}>
          {m.icon}
        </span>
      </Tooltip>
    );
  };

  const columns = [
    {
      title: 'Event Name',
      key: 'eventName',
      width: 220,
      render: (_, record) => {
        const name = record.eventLocation?.eventName || record.userInfo?.customData?.businessEventName || '-';
        return (
          <Tooltip title={name !== '-' ? name : undefined}>
            <div
              style={{
                fontWeight: 500,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.35
              }}
            >
              {name}
            </div>
          </Tooltip>
        );
      }
    },
    {
      title: 'Client',
      key: 'patient',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {record.userInfo.firstName} {record.userInfo.lastName}
            {record.metadata?.isDoubleBooked && (
              <Tag 
                color="warning" 
                style={{ 
                  fontSize: '10px', 
                  padding: '0 4px', 
                  lineHeight: '16px',
                  marginLeft: '4px'
                }}
              >
                Double-Booked
              </Tag>
            )}
          </div>
          <div style={{ color: '#666', fontSize: '12px' }}>
            {record.userInfo.email}
          </div>
          <div style={{ color: '#666', fontSize: '12px' }}>
            {record.userInfo.phone}
          </div>
        </div>
      )
    },
    {
      title: 'Service & Address',
      key: 'eventType',
      width: 190,
      render: (_, record) => {
        const addressParts = [
          record.eventLocation?.address || record.userInfo?.customData?.address1,
          record.eventLocation?.city || record.userInfo?.customData?.city,
          record.eventLocation?.state || record.userInfo?.customData?.state,
          record.eventLocation?.zipCode || record.userInfo?.customData?.zipCode,
        ].filter(Boolean);
        const addressStr = addressParts.length
          ? `${addressParts[0]}${addressParts.slice(1).length ? ', ' + addressParts.slice(1).join(', ') : ''}`
          : null;
        return (
          <div>
            {addressStr && (
              <Tooltip title={addressStr}>
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#333',
                    lineHeight: 1.3,
                    marginBottom: 4,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {addressStr}
                </div>
              </Tooltip>
            )}
            <Tooltip title={record.eventType.name}>
              <Tag
                color={record.eventType.color}
                style={{
                  opacity: 0.75,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                  verticalAlign: 'middle'
                }}
              >
                {record.eventType.name}
              </Tag>
            </Tooltip>
          </div>
        );
      }
    },
    {
      title: 'Event Time',
      key: 'bookingDate',
      width: 200,
      sorter: true,
      sortOrder: sortField === 'bookingDate' ? sortOrder : null,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
            {moment(record.bookingDate).format('MMM D, YYYY')}
            {record.endDate && record.isMultiDay && (
              <span> – {moment(record.endDate).format('MMM D, YYYY')}</span>
            )}
          </div>
          {record.isMultiDay && record.endDate && (
            <div style={{ 
              color: '#1890ff', 
              fontSize: '11px',
              background: '#e6f7ff',
              display: 'inline-block',
              padding: '0 6px',
              borderRadius: '3px',
              marginBottom: '2px'
            }}>
              {moment(record.endDate).diff(moment(record.bookingDate), 'days') + 1}-day event
            </div>
          )}
          <div style={{ color: '#666' }}>
            {record.timeSlot ? 
              `${moment(record.timeSlot.startTime).format('h:mm A')} - ${moment(record.timeSlot.endTime).format('h:mm A')}` :
              moment(record.bookingDate).format('h:mm A')
            }
          </div>
        </div>
      )
    },
    {
      title: 'Via',
      key: 'source',
      width: 70,
      align: 'center',
      render: (_, record) => renderSource(record.source)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      sorter: true,
      sortOrder: sortField === 'status' ? sortOrder : null,
      render: (status, record) => (
        <Select
          value={status}
          size="middle"
          style={{ width: 140 }}
          onChange={(newStatus) => handleStatusChange(record._id, newStatus)}
          disabled={status === 'archived'}
          onClick={(e) => e.stopPropagation()}
        >
          <Select.Option value="pending">
            <Tag color="gold" style={{ margin: 0, fontSize: '13px' }}>Pending</Tag>
          </Select.Option>
          <Select.Option value="confirmed">
            <Tag color="blue" style={{ margin: 0, fontSize: '13px' }}>Confirmed</Tag>
          </Select.Option>
          <Select.Option value="completed">
            <Tag color="green" style={{ margin: 0, fontSize: '13px' }}>Completed</Tag>
          </Select.Option>
          <Select.Option value="cancelled">
            <Tag color="red" style={{ margin: 0, fontSize: '13px' }}>Cancelled</Tag>
          </Select.Option>
          <Select.Option value="no-show">
            <Tag color="orange" style={{ margin: 0, fontSize: '13px' }}>No Show</Tag>
          </Select.Option>
        </Select>
      )
    },
    {
      title: 'App',
      key: 'appEnabled',
      width: 90,
      align: 'center',
      render: (_, record) => {
        const isVisible = record.appEnabled || false;
        return (
          <Tooltip title={isVisible ? 'Visible in HCW app' : 'Hidden from HCW app'}>
            <Switch
              size="small"
              checked={isVisible}
              onChange={(checked) => handleAppVisibilityToggle(record, checked)}
              onClick={(checked, e) => e.stopPropagation()}
            />
          </Tooltip>
        );
      }
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, record) => (
        <Dropdown
          menu={{ items: getMenuItems(record) }}
          trigger={['click']}
        >
          <Button type="text" icon={<FontAwesomeIcon icon={faEllipsisV} />} />
        </Dropdown>
      )
    },
    {
      // Flexible spacer soaks up leftover width so the fixed columns above
      // keep their exact sizes instead of stretching proportionally.
      title: '',
      key: 'spacer'
    }
  ];

  const renderBookingDetailsModal = () => {
    if (!selectedBooking) return null;

    const { userInfo, eventType, timeSlot, bookingDate, status, createdAt } = selectedBooking;
    const bookingEndDate = selectedBooking.endDate;
    const isMultiDay = selectedBooking.isMultiDay;

    const eventName =
      selectedBooking.eventLocation?.eventName ||
      userInfo?.customData?.businessEventName ||
      `${userInfo?.firstName || ''} ${userInfo?.lastName || ''}`.trim() ||
      'Booking Details';

    const accent = eventType?.color || COLORS.$primaryBlue || '#1677ff';

    const statusMeta = {
      pending:   { label: 'Pending',   bg: '#fff7e6', fg: '#d48806', dot: '#fa8c16' },
      confirmed: { label: 'Confirmed', bg: '#e6f4ff', fg: '#0958d9', dot: '#1677ff' },
      completed: { label: 'Completed', bg: '#f6ffed', fg: '#389e0d', dot: '#52c41a' },
      cancelled: { label: 'Cancelled', bg: '#fff1f0', fg: '#cf1322', dot: '#ff4d4f' },
      'no-show': { label: 'No Show',   bg: '#fff2e8', fg: '#d4380d', dot: '#fa541c' },
      archived:  { label: 'Archived',  bg: '#f5f5f5', fg: '#595959', dot: '#8c8c8c' },
    }[status] || { label: status, bg: '#f5f5f5', fg: '#595959', dot: '#8c8c8c' };

    const addressParts = [
      selectedBooking.eventLocation?.address || userInfo?.customData?.address1,
      selectedBooking.eventLocation?.city || userInfo?.customData?.city,
      selectedBooking.eventLocation?.state || userInfo?.customData?.state,
      selectedBooking.eventLocation?.zipCode || userInfo?.customData?.zipCode,
    ].filter(Boolean);
    const addressStr = addressParts.length
      ? `${addressParts[0]}${addressParts.slice(1).length ? ', ' + addressParts.slice(1).join(', ') : ''}`
      : null;

    const effectiveEndDate = selectedBooking.endDate || selectedBooking.bookingDate;
    const isFutureEvent = moment(effectiveEndDate).isAfter(moment(), 'day');
    const isConfirmed = status === 'confirmed';

    const Section = ({ title, children, columns = 2 }) => (
      <div style={{
        background: '#fff',
        border: '1px solid #eef0f4',
        borderRadius: 12,
        padding: '16px 18px',
        boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
        marginTop: 12,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 14,
          fontWeight: 600,
          color: '#1f2937',
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: accent }} />
          {title}
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
          gap: '14px 24px',
        }}>
          {children}
        </div>
      </div>
    );

    const Field = ({ label, children, span = 1 }) => (
      <div style={{ gridColumn: `span ${span}`, minWidth: 0 }}>
        <div style={{
          fontSize: 11,
          color: '#6b7280',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          marginBottom: 4,
          fontWeight: 600,
        }}>
          {label}
        </div>
        <div style={{ fontSize: 14, color: '#111827', fontWeight: 500, wordBreak: 'break-word' }}>
          {children}
        </div>
      </div>
    );

    const customData = userInfo?.customData || {};
    const hasAdditional = !!(
      selectedBooking.eventLocation?.eventName ||
      customData.businessEventName ||
      customData.pointOfContactName ||
      customData.pointOfContactInfo ||
      customData.eventStartTime ||
      customData.screeningStartTime ||
      customData.indoorOutdoorEvent ||
      customData.anticipatedParticipants ||
      customData.volunteersAvailable
    );

    return (
      <Modal
        title={null}
        open={detailsModalVisible}
        onCancel={handleCloseDetailsModal}
        footer={null}
        width={760}
        styles={{
          body: { padding: 0, background: '#f7f8fa', borderRadius: 12 },
          content: { padding: 0, borderRadius: 12, overflow: 'hidden' },
        }}
      >
        {/* Hero header */}
        <div style={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          padding: '24px 28px 22px',
          color: '#fff',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              borderRadius: 999,
              background: 'rgba(255,255,255,0.18)',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: 0.3,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />
              {eventType?.name || 'Service'}
            </span>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              borderRadius: 999,
              background: statusMeta.bg,
              color: statusMeta.fg,
              fontSize: 12,
              fontWeight: 600,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusMeta.dot }} />
              {statusMeta.label}
            </span>
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.25, marginBottom: 6 }}>
            {eventName}
          </div>
          <div style={{ fontSize: 13, opacity: 0.92 }}>
            {moment(bookingDate).format('dddd, MMM D, YYYY')}
            {isMultiDay && bookingEndDate && ` – ${moment(bookingEndDate).format('MMM D, YYYY')}`}
            {timeSlot && ` · ${moment(timeSlot.startTime).format('h:mm A')} – ${moment(timeSlot.endTime).format('h:mm A')}`}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '4px 18px 18px', maxHeight: '60vh', overflowY: 'auto' }}>
          <Section title="Service & Address">
            <Field label="Service Type">
              <Tag color={eventType.color} style={{ fontSize: 13, padding: '2px 10px', borderRadius: 6 }}>
                {eventType.name}
              </Tag>
            </Field>
            <Field label="Duration">{eventType.duration} minutes</Field>
            {addressStr && (
              <Field label="Address" span={2}>{addressStr}</Field>
            )}
          </Section>

          <Section title="Schedule">
            <Field label="Appointment Date">
              {moment(bookingDate).format('MMM D, YYYY')}
              {isMultiDay && bookingEndDate && (
                <span style={{ color: '#6b7280' }}>
                  {' – '}{moment(bookingEndDate).format('MMM D, YYYY')}
                  <Tag color="blue" style={{ marginLeft: 8 }}>
                    {moment(bookingEndDate).diff(moment(bookingDate), 'days') + 1}-day
                  </Tag>
                </span>
              )}
            </Field>
            {timeSlot && (
              <Field label="Event Time">
                {moment(timeSlot.startTime).format('h:mm A')} – {moment(timeSlot.endTime).format('h:mm A')}
              </Field>
            )}
            <Field label="Booked">{moment(createdAt).format('MMM D, YYYY h:mm A')}</Field>
            <Field label="Booked Via">{renderSource(selectedBooking.source)}</Field>
          </Section>

          <Section title="Client">
            <Field label="Name">{userInfo.firstName} {userInfo.lastName}</Field>
            <Field label="Phone">{userInfo.phone || '—'}</Field>
            <Field label="Email" span={2}>{userInfo.email || '—'}</Field>
          </Section>

          <Section title="Booking Controls">
            <Field label="Status">
              <Select
                value={status}
                style={{ width: 180 }}
                onChange={(newStatus) => handleStatusChange(selectedBooking._id, newStatus)}
                disabled={status === 'archived'}
              >
                <Select.Option value="pending"><Tag color="gold">Pending</Tag></Select.Option>
                <Select.Option value="confirmed"><Tag color="blue">Confirmed</Tag></Select.Option>
                <Select.Option value="completed"><Tag color="green">Completed</Tag></Select.Option>
                <Select.Option value="cancelled"><Tag color="red">Cancelled</Tag></Select.Option>
                <Select.Option value="no-show"><Tag color="orange">No Show</Tag></Select.Option>
                {status === 'archived' && (
                  <Select.Option value="archived"><Tag color="gray">Archived</Tag></Select.Option>
                )}
              </Select>
            </Field>
            <Field label="Pre-Registration">
              {!isFutureEvent ? (
                <Tooltip title="Pre-registration is only available for future events">
                  <span style={{ color: '#9ca3af', fontSize: 12 }}>N/A</span>
                </Tooltip>
              ) : !isConfirmed ? (
                <Tooltip title="Event must be confirmed to enable pre-registration">
                  <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>
                </Tooltip>
              ) : (
                <Tooltip title={selectedBooking.allowPreRegistration ? 'Pre-registration is open' : 'Enable pre-registration for this event'}>
                  <Switch
                    checked={selectedBooking.allowPreRegistration || false}
                    onChange={(checked) => handlePreRegistrationToggle(selectedBooking, checked)}
                    style={{ backgroundColor: selectedBooking.allowPreRegistration ? '#722ed1' : undefined }}
                  />
                </Tooltip>
              )}
            </Field>
          </Section>

          {hasAdditional && (
            <Section title="Additional Information" columns={2}>
              {(selectedBooking.eventLocation?.eventName || customData.businessEventName) && (
                <Field label="Business / Event Name" span={2}>
                  {selectedBooking.eventLocation?.eventName || customData.businessEventName}
                </Field>
              )}
              {customData.pointOfContactName && (
                <Field label="Point of Contact">{customData.pointOfContactName}</Field>
              )}
              {customData.pointOfContactInfo && (
                <Field label="Contact Info">{customData.pointOfContactInfo}</Field>
              )}
              {customData.eventStartTime && (
                <Field label="Event Start Time">{customData.eventStartTime}</Field>
              )}
              {customData.screeningStartTime && (
                <Field label="Screening Start Time">{customData.screeningStartTime}</Field>
              )}
              {customData.indoorOutdoorEvent && (
                <Field label="Environment">
                  {customData.indoorOutdoorEvent === 'indoor' ? 'Indoor Event' : 'Outdoor Event'}
                </Field>
              )}
              {customData.anticipatedParticipants && (
                <Field label="Anticipated Participants">{customData.anticipatedParticipants}</Field>
              )}
              {customData.volunteersAvailable && (
                <Field label="Volunteers Available">
                  {customData.volunteersAvailable === 'yes' ? 'Yes' : 'No'}
                </Field>
              )}
            </Section>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          padding: '14px 20px',
          background: '#fff',
          borderTop: '1px solid #eef0f4',
        }}>
          <Button onClick={handleCloseDetailsModal}>Close</Button>
          <Button
            type="primary"
            icon={<FontAwesomeIcon icon={faEdit} />}
            onClick={() => {
              handleCloseDetailsModal();
              handleEditBooking(selectedBooking);
            }}
            disabled={status === 'archived'}
          >
            Edit Booking
          </Button>
        </div>
      </Modal>
    );
  };

  const renderBookingCard = (record) => {
    const eventName =
      record.eventLocation?.eventName ||
      record.userInfo?.customData?.businessEventName ||
      `${record.userInfo?.firstName || ''} ${record.userInfo?.lastName || ''}`.trim() ||
      'Booking';
    const clientName = `${record.userInfo?.firstName || ''} ${record.userInfo?.lastName || ''}`.trim();
    const phone = record.userInfo?.phone;
    const pill = STATUS_PILL[record.status] || STATUS_PILL.pending;
    const timeStr = record.timeSlot
      ? `${moment(record.timeSlot.startTime).format('h:mm A')} – ${moment(record.timeSlot.endTime).format('h:mm A')}`
      : moment(record.bookingDate).format('h:mm A');

    return (
      <BookingCard key={record._id} onClick={() => handleRowClick(record)}>
        <div className="card-head">
          <div className="title">{eventName}</div>
          <BookingCardStatus $bg={pill.bg} $fg={pill.fg}>{pill.label}</BookingCardStatus>
        </div>
        {(clientName || phone) && (
          <div className="client">
            {clientName}{clientName && phone ? ' · ' : ''}{phone}
          </div>
        )}
        {record.eventType?.name && (
          <div className="unit">
            <Tag color={record.eventType.color} style={{ margin: 0 }}>
              {record.eventType.name}
            </Tag>
          </div>
        )}
        <div className="meta">
          <span className="meta-item">
            <CalendarOutlined />
            {moment(record.bookingDate).format('MMM D')}
            {record.isMultiDay && record.endDate && (
              <> – {moment(record.endDate).format('MMM D')}</>
            )}
          </span>
          <span className="meta-item">
            <ClockCircleOutlined />
            {timeStr}
          </span>
        </div>
      </BookingCard>
    );
  };

  return (
    <TabContentWrapper>
      {/* Mobile header: search + filter sheet trigger + New */}
      <MobileFilterBar>
        <div className="search">
          <Input
            placeholder="Search bookings..."
            prefix={<FontAwesomeIcon icon={faSearch} />}
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            allowClear
          />
        </div>
        <button
          type="button"
          className={`icon-btn${hasActiveFilters() ? ' active' : ''}`}
          aria-label="Filters"
          onClick={() => setFilterDrawerVisible(true)}
        >
          <FontAwesomeIcon icon={faSliders} />
          {(() => {
            const n = [filters.eventType, filters.dateRange].filter(Boolean).length;
            return n > 0 ? <span className="badge">{n}</span> : null;
          })()}
        </button>
        <Button
          type="primary"
          className="new-btn"
          icon={<PlusOutlined />}
          onClick={() => setCreateModalVisible(true)}
          style={{ background: COLORS.$primaryBlue, borderColor: COLORS.$primaryBlue }}
        >
          New
        </Button>
      </MobileFilterBar>

      <StatusChipsRow>
        {STATUS_CHIPS.map((chip) => {
          const active = filters.status === chip.value;
          const count = stats ? stats[chip.countKey] : undefined;
          return (
            <FilterChip
              key={chip.label}
              type="button"
              $active={active}
              $countColor={chip.color}
              onClick={() => handleFilterChange('status', chip.value)}
            >
              {chip.label}
              {typeof count === 'number' && <span className="count">{count}</span>}
            </FilterChip>
          );
        })}
      </StatusChipsRow>

      <Row gutter={[0, 8]}>

        <Col span={24}>
          <DesktopFilters>
          <AnimatedFiltersRow>
            <BookingFilters>
              <Row gutter={[8, 8]} align="middle" style={{ width: '100%' }}>
                <Col flex="auto" className="search-input">
                  <Input
                    placeholder="Search by name, email, or code..."
                    prefix={<FontAwesomeIcon icon={faSearch} />}
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </Col>

                <Col flex="none" className="date-range-filter">
                  <RangePicker
                    size="large"
                    value={filters.dateRange}
                    onChange={(dates) => handleFilterChange('dateRange', dates)}
                    format="MMM D, YYYY"
                    placeholder={['Start date', 'End date']}
                    allowClear
                    style={{ width: 280 }}
                  />
                </Col>
                
                <Col flex="none" className="filter-controls">
                  <Button
                    type="default"
                    icon={<FontAwesomeIcon icon={faFilter} />}
                    onClick={() => setFilterDrawerVisible(true)}
                    style={{
                      marginRight: '8px',
                      color: hasActiveFilters() ? COLORS.$primaryBlue : COLORS.textMedium,
                      borderColor: hasActiveFilters() ? COLORS.$primaryBlue : COLORS.border,
                      background: hasActiveFilters() ? `${COLORS.$primaryBlue}08` : 'white',
                      position: 'relative',
                    }}
                  >
                    Filter
                    {hasActiveFilters() && (
                      <span style={{
                        position: 'absolute',
                        top: '-6px',
                        right: '-6px',
                        background: COLORS.$primaryBlue,
                        color: 'white',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '600',
                        border: '2px solid white'
                      }}>
                        {[filters.status, filters.eventType, filters.search, filters.dateRange].filter(Boolean).length}
                      </span>
                    )}
                  </Button>
                  <Dropdown
                    menu={{
                      items: [
                        { key: 'csv', label: 'Download CSV', icon: <DownloadOutlined />, onClick: handleExportCSV },
                        { key: 'excel', label: 'Download Excel', icon: <FileExcelOutlined />, onClick: handleExportExcel }
                      ]
                    }}
                    trigger={['click']}
                  >
                    <Button
                      icon={<DownloadOutlined />}
                      loading={exporting}
                      style={{
                        marginRight: '8px'
                      }}
                    >
                      {selectedRowKeys.length > 0 ? `Export (${selectedRowKeys.length})` : 'Export'}
                    </Button>
                  </Dropdown>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateModalVisible(true)}
                    style={{
                      background: COLORS.$primaryBlue,
                      borderColor: COLORS.$primaryBlue,
                    }}
                  >
                    New Booking
                  </Button>
                </Col>
              </Row>
            </BookingFilters>
          </AnimatedFiltersRow>
          </DesktopFilters>
        </Col>

        <Col span={24}>
          <AnimatedTableSection style={{ position: 'relative' }}>
            {loading && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 10,
                minHeight: '200px'
              }}>
                <HeartLoader size={48} />
              </div>
            )}
            <DesktopTableWrapper>
              <BookingTable
                columns={columns}
                dataSource={bookings}
                rowKey="_id"
                rowSelection={rowSelection}
                className="event-management-pagination"
                onChange={handleTableChange}
                onRow={(record) => ({
                  onClick: (e) => {
                    // Don't open drawer when clicking interactive elements inside the row
                    if (
                      e.target.closest('.ant-checkbox-wrapper') ||
                      e.target.closest('.ant-dropdown-trigger') ||
                      e.target.closest('.ant-dropdown') ||
                      e.target.closest('button') ||
                      e.target.closest('a') ||
                      e.target.closest('.ant-tag')
                    ) return;
                    handleRowClick(record);
                  },
                  style: { cursor: 'pointer' }
                })}
                scroll={{ x: 1200 }}
                pagination={{
                  current: pagination.page,
                  pageSize: pagination.limit,
                  total: pagination.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} bookings`,
                  pageSizeOptions: ['10', '20', '50', '100']
                }}
              />
            </DesktopTableWrapper>

            <MobileBookingCards>
              {bookings.length === 0 && !loading ? (
                <Empty description="No bookings found" />
              ) : (
                bookings.map(renderBookingCard)
              )}
            </MobileBookingCards>

            <MobileCardsPagination>
              {(() => {
                const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));
                return (
                  <>
                    <button
                      type="button"
                      className="pg-btn"
                      disabled={pagination.page <= 1}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    >
                      <LeftOutlined /> Prev
                    </button>
                    <span className="pg-info">
                      Page <strong>{pagination.page}</strong> of {totalPages}
                    </span>
                    <button
                      type="button"
                      className="pg-btn"
                      disabled={pagination.page >= totalPages}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    >
                      Next <RightOutlined />
                    </button>
                  </>
                );
              })()}
            </MobileCardsPagination>
          </AnimatedTableSection>
        </Col>
      </Row>

      {/* Booking Details Modal */}
      {renderBookingDetailsModal()}

      {/* Edit Booking Modal */}
      <EditBookingModal
        visible={editModalVisible}
        booking={selectedBooking}
        onCancel={handleCloseEditModal}
        onSuccess={handleEditSuccess}
      />

      {/* Filter Drawer */}
      <BookingFilterDrawer
        visible={filterDrawerVisible}
        onClose={() => setFilterDrawerVisible(false)}
        filters={filters}
        onFilterChange={handleFilterChange}
        eventTypes={eventTypes}
        onExport={handleLegacyExport}
        hasBookings={bookings.length > 0}
        isMobile={isMobile}
      />

      {/* Create Booking Modal */}
      <CreateBookingModal
        visible={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        onSuccess={() => {
          setCreateModalVisible(false);
          loadBookings(); // Refresh the bookings list
        }}
      />

      {/* Publish to App Modal — collects mobile-event fields for a booking */}
      <Modal
        title="Publish to Deployed Hearts app"
        open={appModalVisible}
        onOk={handleAppPublishSubmit}
        onCancel={handleAppPublishCancel}
        okText="Publish event"
        confirmLoading={appPublishing}
        destroyOnClose
      >
        <p style={{ color: '#666', marginBottom: 16 }}>
          {appModalBooking?.eventLocation?.eventName ||
            appModalBooking?.userInfo?.customData?.businessEventName ||
            'This booking'}{' '}
          will appear as a mobile event. Add the staffing and pay details below.
        </p>
        <Form form={appPublishForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="payRate"
                label="Pay rate ($)"
                rules={[{ required: true, message: 'Enter a pay rate' }]}
              >
                <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="payType" label="Pay type">
                <Select
                  options={[
                    { value: 'hourly', label: 'Hourly' },
                    { value: 'flat', label: 'Flat' }
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="estimatedHours" label="Estimated hours">
                <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="requiredStaff"
                label="Required staff"
                rules={[{ required: true, message: 'Enter required staff' }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="estimatedAttendees" label="Estimated attendees">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Members Drawer — shows all members tied to a booking */}
      <Drawer
        title={
          <div>
            <div style={{ fontWeight: 700, fontSize: '18px' }}>
              {membersDrawerBooking?.eventLocation?.eventName ||
                membersDrawerBooking?.userInfo?.customData?.businessEventName ||
                membersDrawerBooking?.companyName ||
                'Event Members'}
            </div>
            <div style={{ fontSize: '13px', color: '#666', fontWeight: 400 }}>
              {membersDrawerBooking?.bookingDate &&
                moment(membersDrawerBooking.bookingDate).format('MMM D, YYYY')}
              {' · '}
              {membersDrawerVisits.length} member{membersDrawerVisits.length === 1 ? '' : 's'}
            </div>
          </div>
        }
        open={membersDrawerVisible}
        onClose={() => setMembersDrawerVisible(false)}
        width={900}
        destroyOnClose
      >
        {membersDrawerBooking && (() => {
          const b = membersDrawerBooking;
          const effectiveEndDate = b.endDate || b.bookingDate;
          const isFutureEvent = moment(effectiveEndDate).isAfter(moment(), 'day');
          const isConfirmed = b.status === 'confirmed';
          const addressParts = [
            b.eventLocation?.address || b.userInfo?.customData?.address1,
            b.eventLocation?.city || b.userInfo?.customData?.city,
            b.eventLocation?.state || b.userInfo?.customData?.state,
            b.eventLocation?.zipCode || b.userInfo?.customData?.zipCode,
          ].filter(Boolean);
          const addressStr = addressParts.length
            ? `${addressParts[0]}${addressParts.slice(1).length ? ', ' + addressParts.slice(1).join(', ') : ''}`
            : null;
          return (
            <div style={{ marginBottom: '16px' }}>
              <Descriptions title="Service & Address" column={2} bordered size="small">
                <Descriptions.Item label="Service Type" span={2}>
                  {b.eventType?.name ? (
                    <Tag color={b.eventType.color} style={{ fontSize: '14px' }}>
                      {b.eventType.name}
                    </Tag>
                  ) : (
                    <span style={{ color: '#999' }}>—</span>
                  )}
                </Descriptions.Item>
                {addressStr && (
                  <Descriptions.Item label="Address" span={2}>
                    {addressStr}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Booked">
                  {b.createdAt ? moment(b.createdAt).format('MMM D, YYYY h:mm A') : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Pre-Registration">
                  {!isFutureEvent ? (
                    <Tooltip title="Pre-registration is only available for future events">
                      <span style={{ color: '#999', fontSize: '12px' }}>N/A</span>
                    </Tooltip>
                  ) : !isConfirmed ? (
                    <Tooltip title="Event must be confirmed to enable pre-registration">
                      <span style={{ color: '#999', fontSize: '12px' }}>-</span>
                    </Tooltip>
                  ) : (
                    <Tooltip title={b.allowPreRegistration ? 'Pre-registration is open' : 'Enable pre-registration for this event'}>
                      <Switch
                        size="small"
                        checked={b.allowPreRegistration || false}
                        onChange={(checked) => handlePreRegistrationToggle(b, checked)}
                        style={{ backgroundColor: b.allowPreRegistration ? '#722ed1' : undefined }}
                      />
                    </Tooltip>
                  )}
                </Descriptions.Item>
              </Descriptions>
            </div>
          );
        })()}

        <div style={{ marginBottom: '16px' }}>
          <Input
            placeholder="Search by name or email..."
            prefix={<SearchOutlined />}
            value={membersDrawerSearch}
            onChange={(e) => setMembersDrawerSearch(e.target.value)}
            allowClear
            style={{ maxWidth: '400px' }}
          />
        </div>
        <BookingTable
          rowKey={(r) => r._id || r.visitId || r.patientId}
          loading={membersDrawerLoading}
          dataSource={(() => {
            const q = membersDrawerSearch.trim().toLowerCase();
            if (!q) return membersDrawerVisits;
            return membersDrawerVisits.filter(v => {
              const name = `${v.firstName || ''} ${v.lastName || ''}`.toLowerCase();
              const email = (v.emailAddress || '').toLowerCase();
              return name.includes(q) || email.includes(q);
            });
          })()}
          onRow={(record) => ({
            onClick: () => handleMemberClick(record),
            style: { cursor: 'pointer' }
          })}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No members found for this event"
              />
            )
          }}
          columns={[
            {
              title: 'Name',
              key: 'name',
              render: (_, r) => (
                <div style={{ fontWeight: 600 }}>
                  {r.firstName || r.lastName
                    ? `${r.firstName || ''} ${r.lastName || ''}`.trim()
                    : '—'}
                </div>
              )
            },
            {
              title: 'Email',
              key: 'email',
              render: (_, r) => r.emailAddress || '—'
            },
            {
              title: 'Phone',
              key: 'phone',
              render: (_, r) => r.phoneNumber || '—'
            },
            {
              title: 'Status',
              key: 'status',
              width: 130,
              render: (_, r) => {
                const colorMap = {
                  pre_registered: 'gold',
                  pending_intake: 'gold',
                  in_intake: 'blue',
                  in_screening: 'blue',
                  completed: 'green',
                  no_show: 'red',
                  archived: 'default'
                };
                return (
                  <Tag color={colorMap[r.status] || 'default'}>
                    {(r.status || 'unknown').replace(/_/g, ' ')}
                  </Tag>
                );
              }
            },
            {
              title: 'Check-in',
              key: 'checkInTime',
              width: 160,
              render: (_, r) => r.checkInTime
                ? moment(r.checkInTime).format('MMM D, h:mm A')
                : '—'
            },
            {
              title: '',
              key: 'edit',
              width: 60,
              render: (_, r) => (
                <Tooltip title="Open member record">
                  <Button
                    type="text"
                    icon={<FontAwesomeIcon icon={faUserEdit} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMemberClick(r);
                    }}
                  />
                </Tooltip>
              )
            }
          ]}
          pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
          scroll={{ x: 700 }}
        />
      </Drawer>
    </TabContentWrapper>
  );
};

export default BookingManager;
